import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  createSerialFormatterItem,
  isSerialFormatterSourceUsed,
  parseSerialFormatter,
  pruneSerialFormatterItemsBySpecIds,
  serializeSerialFormatter,
  shouldDeferPruneSerialFormatter,
} from '#/modules/imsMgmt/settings/mtrTemplate/helper/serialFormatterSerialize';


function resolveOptionLabel(options, value, fallback = '') {
  const matched = options.find((option) => option.value === value);
  return matched?.label ?? fallback;
}

function buildDefaultItems(mtrStructMainId, mtrStructSubId, structMainOptions, structSubOptions) {
  if (!mtrStructMainId || !mtrStructSubId) {
    return [];
  }

  return [
    createSerialFormatterItem(
      'manufacture',
      '',
      '製造商',
    ),
    createSerialFormatterItem(
      'mtr_struct_main_id',
      mtrStructMainId,
      resolveOptionLabel(structMainOptions, mtrStructMainId, '主分類'),
    ),
    createSerialFormatterItem(
      'mtr_struct_sub_id',
      mtrStructSubId,
      resolveOptionLabel(structSubOptions, mtrStructSubId, '次分類'),
    ),
  ];
}

function useMtrSerialFormatterSourceOptions({
  mtrStructMainId,
  mtrStructSubId,
  specs,
  specOptions,
  structMainOptions,
  structSubOptions,
}) {
  return useMemo(() => {
    const sources = [];

    sources.push({
      key: 'manufacture',
      type: 'manufacture',
      value: '',
      label: '製造商',
      chipLabel: '製造商',
    });

    if (mtrStructMainId) {
      sources.push({
        key: `main-${mtrStructMainId}`,
        type: 'mtr_struct_main_id',
        value: mtrStructMainId,
        label: resolveOptionLabel(structMainOptions, mtrStructMainId, '主分類'),
        chipLabel: '主分類',
      });
    }

    if (mtrStructSubId) {
      sources.push({
        key: `sub-${mtrStructSubId}`,
        type: 'mtr_struct_sub_id',
        value: mtrStructSubId,
        label: resolveOptionLabel(structSubOptions, mtrStructSubId, '次分類'),
        chipLabel: '次分類',
      });
    }

    const selectedSpecIds = new Set(
      (specs ?? []).map((rule) => rule?.mtrSpecId).filter(Boolean),
    );

    specOptions
      .filter((option) => selectedSpecIds.has(option.value))
      .forEach((option) => {
        const displayName = option.name || option.code;
        sources.push({
          key: `spec-${option.value}`,
          type: 'mtr_spec',
          value: option.value,
          label: displayName,
          chipLabel: displayName,
        });
      });

    return sources;
  }, [
    mtrStructMainId,
    mtrStructSubId,
    specs,
    specOptions,
    structMainOptions,
    structSubOptions,
  ]);
}

function useMtrSerialFormatter({
  setValue,
  initialRaw,
  mtrStructMainId,
  mtrStructSubId,
  specs,
  specOptions,
  structMainOptions,
  structSubOptions,
}) {
  const [items, setItems] = useState(() => parseSerialFormatter(initialRaw));
  const [hasSeeded, setHasSeeded] = useState(() => parseSerialFormatter(initialRaw).length > 0);

  const sourceOptions = useMtrSerialFormatterSourceOptions({
    mtrStructMainId,
    mtrStructSubId,
    specs,
    specOptions,
    structMainOptions,
    structSubOptions,
  });

  const availableSourceOptions = useMemo(
    () => sourceOptions.filter((source) => !isSerialFormatterSourceUsed(items, source)),
    [sourceOptions, items],
  );

  const syncFormValue = useCallback((nextItems) => {
    setValue('serialFormatter', serializeSerialFormatter(nextItems), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue]);

  const updateItems = useCallback((updater) => {
    setItems((prevItems) => {
      const nextItems = typeof updater === 'function' ? updater(prevItems) : updater;
      if (nextItems === prevItems) {
        return prevItems;
      }
      syncFormValue(nextItems);
      return nextItems;
    });
  }, [syncFormValue]);

  // 規格刪列／改選 mtrSpecId：剔除幽靈序號段並寫回 serialFormatter
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- prune when specs shrink
    updateItems((prevItems) => {
      // edit 首載：specs 仍 [] 但 items 已有 mtr_spec → 勿當「清空」剃掉
      if (shouldDeferPruneSerialFormatter(specs, prevItems)) {
        return prevItems;
      }

      const selectedSpecIds = new Set(
        (specs ?? [])
          .map((rule) => String(rule?.mtrSpecId ?? ''))
          .filter(Boolean),
      );

      const pruned = pruneSerialFormatterItemsBySpecIds(prevItems, selectedSpecIds);
      const unchanged = pruned.length === prevItems.length
        && pruned.every((item, index) => (
          item.id === prevItems[index].id
          && item.type === prevItems[index].type
          && String(item.value) === String(prevItems[index].value)
        ));

      return unchanged ? prevItems : pruned;
    });
  }, [specs, updateItems]);

  const trySeedDefaults = useCallback(() => {
    if (hasSeeded || items.length > 0) {
      return;
    }

    if (!mtrStructMainId || !mtrStructSubId) {
      return;
    }

    const defaultItems = buildDefaultItems(
      mtrStructMainId,
      mtrStructSubId,
      structMainOptions,
      structSubOptions,
    );

    if (defaultItems.length === 0) {
      return;
    }

    setHasSeeded(true);
    updateItems(defaultItems);
  }, [
    hasSeeded,
    items.length,
    mtrStructMainId,
    mtrStructSubId,
    structMainOptions,
    structSubOptions,
    updateItems,
  ]);

  const addSourceItem = useCallback((source) => {
    updateItems((prevItems) => {
      if (isSerialFormatterSourceUsed(prevItems, source)) {
        return prevItems;
      }

      return [
        ...prevItems,
        createSerialFormatterItem(source.type, source.value, source.label),
      ];
    });
  }, [updateItems]);

  const addCustomItem = useCallback((value) => {
    const trimmed = String(value ?? '').trim();

    if (!trimmed) {
      return false;
    }

    updateItems((prevItems) => [
      ...prevItems,
      createSerialFormatterItem('custom', trimmed, trimmed),
    ]);

    return true;
  }, [updateItems]);

  const removeItem = useCallback((itemId) => {
    updateItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  }, [updateItems]);

  const reorderItems = useCallback((nextItems) => {
    updateItems(nextItems);
  }, [updateItems]);

  return {
    items,
    sourceOptions,
    availableSourceOptions,
    addSourceItem,
    addCustomItem,
    removeItem,
    reorderItems,
    trySeedDefaults,
  };
}

export default useMtrSerialFormatter;
